import {
  AvlModel,
  CameraConfiguration,
  DigitalSpreaderModel,
  DigitalSpreaderSettings,
  HardwareConfiguration,
  SensorInput,
  SensorType,
  VehicleModel,
} from '../../../../../../shared/models/vehicle.model';

export class VehicleControlUnit {
  avlModel?: AvlModel = AvlModel.NONE;
  lmuId: string;

  cameraConfiguration?: CameraConfiguration;
  digitalSpreader?: DigitalSpreaderConfiguration;

  additionalHardware: VehicleHardware[] = [];

  static fromVehicleModel(vehicle: VehicleModel): VehicleControlUnit {
    const result = new VehicleControlUnit();

    const { lmuId, hardwareConfiguration, cameraConfiguration } = vehicle;

    if (lmuId == null && hardwareConfiguration == null && cameraConfiguration == null) {
      return null;
    }

    result.lmuId = lmuId;
    result.avlModel = hardwareConfiguration?.avlModel || AvlModel.NONE;
    result.cameraConfiguration = cameraConfiguration;
    if (hardwareConfiguration?.digitalSpreader) {
      const { model, settings } = hardwareConfiguration?.digitalSpreader || {};
      result.digitalSpreader = new DigitalSpreaderConfiguration(model, settings);
    }

    let pending2WPlow: VehicleHardware;

    hardwareConfiguration?.sensorInputs?.forEach(input => {
      const hardware = new VehicleHardware();
      switch (input.type) {
        case SensorType.DIGITAL_GRANULAR_SPREADER:
          // don't add hardware
          result.digitalSpreader.granularSpreaderBit = input.bitNumber;
          return;
        case SensorType.DIGITAL_LIQUID_SPREADER:
          result.digitalSpreader.liquidSpreaderBit = input.bitNumber;
          // don't add hardware
          return;

        // @ts-ignore
        case SensorType.PLOW2W:
          if (pending2WPlow) {
            pending2WPlow.plowConfiguration.twoWireOtherConnection = {
              inputNumber: input.id,
              reversed: input.reversed,
              bluetooth: false,
            };
            // don't add additional HW
            return;
          }
          pending2WPlow = hardware;
        // fall through
        case SensorType.PLOW:
          hardware.type = VehicleHardwareType.PLOW;
          hardware.plowConfiguration = new PlowConfiguration(input.label);
          break;

        case SensorType.GRANULAR_SPREADER:
          hardware.type = VehicleHardwareType.GRANULAR_SPREADER;
          hardware.granularSpreaderConfiguration = new GranularSpreaderConfiguration(input.rate);
          break;
        case SensorType.LIQUID_SPREADER:
          hardware.type = VehicleHardwareType.LIQUID_SPREADER;
          hardware.liquidSpreaderConfiguration = new LiquidSpreaderConfiguration(input.rate);
          break;
        case SensorType.MOWER:
          hardware.type = VehicleHardwareType.MOWER;
          break;
        case SensorType.SWEEPER:
          hardware.type = VehicleHardwareType.SWEEPER;
          break;
        default:
          throw Error(`${JSON.stringify(input)} Not supported yet`);
      }
      hardware.reportingBit = input.bitNumber;
      hardware.connection = new InputConnection(input.id, input.reversed, input.bluetooth);

      result.additionalHardware.push(hardware);
    });

    return result;
  }

  toVehicleModel() {
    const { avlModel, lmuId, cameraConfiguration, digitalSpreader, additionalHardware } = this;

    const result: VehicleModel = { lmuId, cameraConfiguration } as VehicleModel;

    if (avlModel) {
      const sensorInputs = [];
      result.hardwareConfiguration = {
        avlModel,
        sensorInputs,
      } as HardwareConfiguration;

      if (digitalSpreader) {
        this.exportDigitalSpreader(digitalSpreader, result);
      }

      additionalHardware.forEach(hw => {
        this.exportVehicleHardware(hw, sensorInputs);
      });
    }

    return result;
  }

  getAvailableBitInRange(inRange: BitRange, usedBits: number[]) {
    const bitRanges = {
      [BitRange.PLOW]: { from: 0, to: 4 },
      [BitRange.GRANULAR_SPREADER]: { from: 5, to: 9 },
      [BitRange.LIQUID_SPREADER]: { from: 10, to: 14 },
      [BitRange.MOWER]: { from: 15, to: 19 },
      [BitRange.SWEEPER]: { from: 20, to: 25 },
    };

    const bitRange = bitRanges[inRange];
    for (let i = bitRange.from; i <= bitRange.to; i++) {
      if (!usedBits.includes(i)) {
        return i;
      }
    }
    throw new Error(`Cannot find available bit in range=${inRange}. Used bits are ${usedBits}`);
  }

  getAvailableInputs(currentIndex: number): Input[] {
    const inputs = {
      1: 'Input 1',
      2: 'Input 2',
      3: 'Input 3',
      4: 'Input 4',
    };

    this.additionalHardware.forEach((hw, index) => {
      if (index === currentIndex) {
        return;
      }
      if (hw.type === VehicleHardwareType.PLOW) {
        if (hw.plowConfiguration.twoWireOtherConnection) {
          delete inputs[hw.plowConfiguration.twoWireOtherConnection.inputNumber];
        }
      }
      delete inputs[hw.connection.inputNumber];
    });

    // ProHub has only inputs 1 and 2
    if (this.avlModel === AvlModel.PLOWOPS_PROHUB) {
      delete inputs[3];
      delete inputs[4];
    }

    return Object.keys(inputs).map(key => {
      return {
        inputNumber: Number(key),
        name: inputs[key],
      } as Input;
    });
  }

  private exportDigitalSpreader(digitalSpreader: DigitalSpreaderConfiguration, result: VehicleModel) {
    result.hardwareConfiguration.digitalSpreader = digitalSpreader;
    const sensorInputs = result.hardwareConfiguration.sensorInputs;
    const { readLiquid, liquidMaxRate, readGranular, granularMaxRate } = digitalSpreader.settings;
    if (readLiquid) {
      if (digitalSpreader.liquidSpreaderBit === undefined) {
        digitalSpreader.liquidSpreaderBit = this.getAvailableBitInCategory(BitRange.LIQUID_SPREADER);
      }
      sensorInputs.push({
        id: 7,
        type: SensorType.DIGITAL_LIQUID_SPREADER,
        name: '7 - Virtual',
        rate: liquidMaxRate,
        bitNumber: digitalSpreader.liquidSpreaderBit,
      } as SensorInput);
    }

    if (digitalSpreader.granularSpreaderBit === undefined) {
      digitalSpreader.granularSpreaderBit = this.getAvailableBitInCategory(BitRange.GRANULAR_SPREADER);
    }
    if (readGranular) {
      sensorInputs.push({
        id: 6,
        type: SensorType.DIGITAL_GRANULAR_SPREADER,
        name: '6 - Virtual',
        rate: granularMaxRate,
        bitNumber: digitalSpreader.granularSpreaderBit,
      } as SensorInput);
    }
  }

  private getAvailableBitInCategory(inRange: BitRange) {
    return this.getAvailableBitInRange(inRange, this.getUsedBits());
  }

  private getUsedBits() {
    const bits: (number | undefined)[] = [
      this.digitalSpreader?.granularSpreaderBit,
      this.digitalSpreader?.liquidSpreaderBit,
      ...(this.additionalHardware?.map(value => value.reportingBit) || []),
    ];

    return bits.filter(it => it !== undefined);
  }

  private exportVehicleHardware(hw: VehicleHardware, sensorInputs: SensorInput[]) {
    const name = `Input ${hw.connection.inputNumber}`;

    if (hw.reportingBit === undefined || hw.reportingBit === null) {
      const bitCategoryByHardwareType: { [key in VehicleHardwareType]: BitRange } = {
        [VehicleHardwareType.PLOW]: BitRange.PLOW,
        [VehicleHardwareType.LIQUID_SPREADER]: BitRange.LIQUID_SPREADER,
        [VehicleHardwareType.GRANULAR_SPREADER]: BitRange.GRANULAR_SPREADER,
        [VehicleHardwareType.MOWER]: BitRange.MOWER,
        [VehicleHardwareType.SWEEPER]: BitRange.SWEEPER,
      };
      hw.reportingBit = this.getAvailableBitInCategory(bitCategoryByHardwareType[hw.type]);
    }

    const input = {
      id: hw.connection.inputNumber,
      reversed: hw.connection.reversed,
      bluetooth: hw.connection.bluetooth,
      bitNumber: hw.reportingBit,
      name,
    } as SensorInput;
    sensorInputs.push(input);

    switch (hw.type) {
      case VehicleHardwareType.PLOW: {
        const { twoWireOtherConnection, label } = hw.plowConfiguration;
        input.type = twoWireOtherConnection ? SensorType.PLOW2W : SensorType.PLOW;
        input.label = label;
        if (twoWireOtherConnection) {
          sensorInputs.push({
            type: SensorType.PLOW2W,
            id: twoWireOtherConnection.inputNumber,
            reversed: twoWireOtherConnection.reversed,
            bluetooth: false,
            name: `Input ${twoWireOtherConnection.inputNumber}`,
            label,
            bitNumber: input.bitNumber,
          } as SensorInput);
        }
        break;
      }
      case VehicleHardwareType.LIQUID_SPREADER:
        input.type = SensorType.LIQUID_SPREADER;
        input.rate = hw.liquidSpreaderConfiguration?.rate;
        break;
      case VehicleHardwareType.GRANULAR_SPREADER:
        input.type = SensorType.GRANULAR_SPREADER;
        input.rate = hw.granularSpreaderConfiguration?.rate;
        break;
      case VehicleHardwareType.MOWER:
        input.type = SensorType.MOWER;
        break;
      case VehicleHardwareType.SWEEPER:
        input.type = SensorType.SWEEPER;
        break;
    }
  }
}

export enum VehicleHardwareType {
  PLOW = 'PLOW',
  LIQUID_SPREADER = 'LIQUID_SPREADER',
  GRANULAR_SPREADER = 'GRANULAR_SPREADER',
  MOWER = 'MOWER',
  SWEEPER = 'SWEEPER',
}

export class VehicleHardware {
  type: VehicleHardwareType;

  connection: InputConnection;
  reportingBit?: number = undefined;

  plowConfiguration?: PlowConfiguration;
  granularSpreaderConfiguration?: GranularSpreaderConfiguration;
  liquidSpreaderConfiguration?: LiquidSpreaderConfiguration;
}

export class PlowConfiguration {
  label?: string;
  twoWireOtherConnection?: InputConnection;

  constructor(label: string, twoWireOtherConnection: InputConnection = null) {
    this.label = label;
    this.twoWireOtherConnection = twoWireOtherConnection;
  }
}

export class DigitalSpreaderConfiguration {
  model: DigitalSpreaderModel;
  settings: DigitalSpreaderSettings;

  granularSpreaderBit?: number;
  liquidSpreaderBit?: number;

  constructor(model: DigitalSpreaderModel, settings: DigitalSpreaderSettings) {
    this.model = model;
    this.settings = settings;
  }
}

export class GranularSpreaderConfiguration {
  rate: number;

  constructor(rate: number) {
    this.rate = rate;
  }
}

export class LiquidSpreaderConfiguration {
  rate: number;

  constructor(rate: number) {
    this.rate = rate;
  }
}

export class InputConnection {
  inputNumber: number;
  reversed = false;
  bluetooth = false;

  constructor(pin: number, reversed: boolean = false, bluetooth: boolean = false) {
    this.inputNumber = pin;
    this.reversed = reversed;
    this.bluetooth = bluetooth;
  }
}

export enum SensorConnectionType {
  SINGLE_WIRE = 'Single Wire',
  TWO_WIRES = 'Two Wires',
  BLUETOOTH = 'Bluetooth',
}

export class Input {
  inputNumber: number;
  name: string;
}

export enum BitRange {
  PLOW = 'PLOW',
  GRANULAR_SPREADER = 'GRANULAR_SPREADER',
  LIQUID_SPREADER = 'LIQUID_SPREADER',
  MOWER = 'MOWER',
  SWEEPER = 'SWEEPER',
}
